/**
 * How a member stands against their monthly model allowance, in the two
 * sizes the organization pages need: a compact cell for tables and lists,
 * and the fuller block the member sheet shows.
 *
 * Spend is only drawn when the page actually has the figure (today: the
 * viewer's own, from `/api/amoreg/model-spend`); otherwise the allowance and
 * the on-demand switch are what is known, and that is what is shown.
 *
 * @module components/settings/MemberUsage
 */
import type { ShowableModelSpend } from "../../state/modelSpend";
import type { OrgTeamMember } from "../../state/orgProfile";
import { formatDayShort, formatGbp } from "../../usage/usageFormat";
import { cn } from "../../lib/utils";
import { summariseModelSpend } from "../usage/ModelSpendBar";
import { allowanceAmountLabel, memberDisplayName, spendOfAllowanceLabel } from "./orgMembers.logic";
import { UsageMeter } from "./settingsCard";

/** "Standard" or "Custom", for the allowance's origin. */
const allowanceKind = (member: Pick<OrgTeamMember, "modelAllowanceGbp">): string =>
  member.modelAllowanceGbp === null ? "Standard" : "Custom";

export function MemberUsageCell({
  member,
  standardGbp,
  spend,
}: {
  readonly member: OrgTeamMember;
  readonly standardGbp: number | null;
  readonly spend: ShowableModelSpend | null;
}) {
  if (spend) {
    return (
      <div className="w-full max-w-44 space-y-1.5">
        <UsageMeter
          size="sm"
          spentGbp={spend.spentGbp}
          includedGbp={spend.includedGbp}
          blocked={spend.state === "blocked"}
          label={`${memberDisplayName(member)}'s model usage this month`}
        />
        <p className="truncate text-xs text-muted-foreground tabular-nums">
          {spendOfAllowanceLabel(spend.spentGbp, spend.includedGbp)}
        </p>
      </div>
    );
  }
  return (
    <div className="min-w-0">
      <p className="truncate text-[13px] text-foreground tabular-nums">
        {allowanceAmountLabel(member, standardGbp)}
        <span className="text-muted-foreground"> / mo</span>
      </p>
      <p
        className={cn(
          "truncate text-xs",
          member.onDemandModelUsage ? "text-muted-foreground" : "text-warning",
        )}
      >
        {allowanceKind(member)} · {member.onDemandModelUsage ? "on demand" : "capped"}
      </p>
    </div>
  );
}

/** The member sheet's usage block: meter when spend is known, the policy always. */
export function MemberUsagePanel({
  member,
  standardGbp,
  spend,
  isViewer,
}: {
  readonly member: OrgTeamMember;
  readonly standardGbp: number | null;
  readonly spend: ShowableModelSpend | null;
  readonly isViewer: boolean;
}) {
  return (
    <div className="space-y-3">
      {spend ? (
        <div className="space-y-2">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-lg font-semibold text-foreground tabular-nums">
              {formatGbp(spend.spentGbp)}
              <span className="ms-1.5 text-xs font-normal text-muted-foreground">
                of {formatGbp(spend.includedGbp)}
              </span>
            </p>
            <p className="text-xs text-muted-foreground">Resets {formatDayShort(spend.resetsOn)}</p>
          </div>
          <UsageMeter
            spentGbp={spend.spentGbp}
            includedGbp={spend.includedGbp}
            blocked={spend.state === "blocked"}
            label={`${memberDisplayName(member)}'s model usage this month`}
          />
          <p
            className={cn(
              "text-xs",
              spend.state === "blocked" ? "font-medium text-error" : "text-muted-foreground",
            )}
          >
            {summariseModelSpend(spend)}
          </p>
        </div>
      ) : null}
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <div className="space-y-0.5">
          <dt className="text-xs text-muted-foreground">Monthly allowance</dt>
          <dd className="text-foreground tabular-nums">
            {allowanceAmountLabel(member, standardGbp)}
            <span className="ms-1.5 text-xs text-muted-foreground">{allowanceKind(member)}</span>
          </dd>
        </div>
        <div className="space-y-0.5">
          <dt className="text-xs text-muted-foreground">Past the allowance</dt>
          <dd className={member.onDemandModelUsage ? "text-foreground" : "text-warning"}>
            {member.onDemandModelUsage ? "Billed on demand" : "Usage pauses"}
          </dd>
        </div>
      </dl>
      {!spend && !isViewer ? (
        <p className="text-xs text-muted-foreground/80">
          Spend this month is visible to {memberDisplayName(member)} on their Usage page.
        </p>
      ) : null}
    </div>
  );
}
